import { useState } from "react";
import { useAdminData, type AdminProduct } from "./AdminDataContext";
import { useAdminAuth } from "./AdminAuthContext";
import { Plus, Pencil, Trash2, Search, AlertTriangle, X, ChevronDown } from "lucide-react";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import { TranslationsPanel, type I18nMap } from "./TranslationsPanel";

const emptyProduct: Omit<AdminProduct, "id"> = {
  slug: "",
  name: "",
  price: 0,
  category: "",
  tag: "",
  description: "",
  details: [{ label: "", value: "" }],
  images: [""],
  relatedIds: [],
  stock_quantity: 0,
  low_stock_threshold: 5,
};

export function AdminProducts() {
  const { products, addProduct, updateProduct, deleteProduct, getNextProductId, categories } = useAdminData();
  const { hasPermission } = useAdminAuth();
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("All");
  const [editing, setEditing] = useState<AdminProduct | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  if (!hasPermission("products")) {
    return <p className="text-[#6F675F]" style={{ fontSize: "14px" }}>You don't have access to this section.</p>;
  }

  const filtered = products.filter((p) => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) || p.slug.includes(search.toLowerCase());
    const matchCat = filterCat === "All" || p.category === filterCat;
    return matchSearch && matchCat;
  });

  const openNew = () => {
    setIsNew(true);
    setEditing({ id: getNextProductId(), ...emptyProduct } as AdminProduct);
  };

  const openEdit = (p: AdminProduct) => {
    setIsNew(false);
    setEditing({ ...p, details: [...p.details], images: [...p.images], relatedIds: [...p.relatedIds] });
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaveError(null);
    // Auto-generate slug if empty
    const product = {
      ...editing,
      slug: editing.slug || editing.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
      details: editing.details.filter((d) => d.label || d.value),
      images: editing.images.filter((i) => i.trim()),
      tag: (editing.tag || undefined) as AdminProduct["tag"],
    };
    const result = isNew ? await addProduct(product) : await updateProduct(product);
    if (result.error) {
      setSaveError(result.error);
      return;
    }
    setEditing(null);
  };

  const handleDelete = async (id: number) => {
    setDeleteError(null);
    const result = await deleteProduct(id);
    if (result.error) {
      setDeleteError(result.error);
      return;
    }
    setDeleteConfirm(null);
  };

  const stockBadge = (p: AdminProduct) => {
    if (p.stock_quantity === 0) return <span className="px-2 py-0.5 bg-[#8B5C58]/10 text-[#8B5C58] rounded-full" style={{ fontSize: "10px" }}>Out of Stock</span>;
    if (p.stock_quantity <= p.low_stock_threshold) return <span className="px-2 py-0.5 bg-[#C7A76C]/10 text-[#C7A76C] rounded-full" style={{ fontSize: "10px" }}>Low Stock</span>;
    return <span className="px-2 py-0.5 bg-[#74856D]/10 text-[#74856D] rounded-full" style={{ fontSize: "10px" }}>In Stock</span>;
  };

  return (
    <div>
      {(saveError || deleteError) && (
        <p className="text-[#8B5C58] mb-4" style={{ fontSize: "12px" }}>{saveError || deleteError}</p>
      )}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <h1
          className="text-[#2D2A26] tracking-[0.08em]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", fontWeight: 400 }}
        >
          Products
        </h1>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] transition-colors tracking-[0.1em] uppercase"
          style={{ fontSize: "11px" }}
        >
          <Plus size={16} strokeWidth={1.5} />
          Add Product
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D8D1C7]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7] focus:outline-none focus:border-[#C7A76C] transition-colors"
            style={{ fontSize: "13px", fontWeight: 300 }}
          />
        </div>
        <div className="relative">
          <select
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            className="appearance-none px-4 pr-10 py-2.5 bg-white border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C] transition-colors cursor-pointer"
            style={{ fontSize: "13px", fontWeight: 300 }}
          >
            <option value="All">All Categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#D8D1C7] pointer-events-none" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-[0_2px_16px_rgba(40,32,20,0.04)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E6E0D7]">
                <th className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "10px", fontWeight: 400 }}>Product</th>
                <th className="text-left px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase hidden md:table-cell" style={{ fontSize: "10px", fontWeight: 400 }}>Category</th>
                <th className="text-right px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "10px", fontWeight: 400 }}>Price</th>
                <th className="text-right px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase hidden sm:table-cell" style={{ fontSize: "10px", fontWeight: 400 }}>Stock</th>
                <th className="text-right px-5 py-3 text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "10px", fontWeight: 400 }}>Status</th>
                <th className="text-right px-5 py-3" style={{ width: "80px" }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((product) => (
                <tr key={product.id} className="border-b border-[#E6E0D7]/50 hover:bg-[#F2EEE8]/50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#F2EEE8] shrink-0">
                        {product.images[0] && (
                          <ImageWithFallback src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-[#2D2A26] truncate" style={{ fontSize: "13px" }}>{product.name}</p>
                        <p className="text-[#D8D1C7] truncate" style={{ fontSize: "11px" }}>{product.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 hidden md:table-cell">
                    <span className="px-2.5 py-1 bg-[#F2EEE8] text-[#6F675F] rounded-full" style={{ fontSize: "11px" }}>
                      {product.category}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right text-[#2D2A26]" style={{ fontSize: "13px" }}>
                    &euro;{product.price}
                  </td>
                  <td className="px-5 py-3 text-right hidden sm:table-cell text-[#2D2A26]" style={{ fontSize: "13px" }}>
                    {product.stock_quantity}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {stockBadge(product)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(product)}
                        className="p-1.5 text-[#6F675F] hover:text-[#6F7F63] transition-colors"
                      >
                        <Pencil size={14} strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(product.id)}
                        className="p-1.5 text-[#6F675F] hover:text-[#8B5C58] transition-colors"
                      >
                        <Trash2 size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-[#D8D1C7]" style={{ fontSize: "14px" }}>
                    No products found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirmation */}
      {deleteConfirm !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={20} className="text-[#8B5C58]" />
              <h3 className="text-[#2D2A26]" style={{ fontSize: "16px" }}>Delete Product?</h3>
            </div>
            <p className="text-[#6F675F] mb-6" style={{ fontSize: "13px", fontWeight: 300 }}>
              This action cannot be undone. The product will be permanently removed.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 border border-[#E6E0D7] text-[#6F675F] rounded-lg hover:bg-[#F2EEE8] transition-colors"
                style={{ fontSize: "12px" }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 py-2.5 bg-[#8B5C58] text-white rounded-lg hover:bg-[#7A4B48] transition-colors"
                style={{ fontSize: "12px" }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit/Create Modal */}
      {editing && (
        <ProductFormModal
          product={editing}
          setProduct={setEditing}
          onSave={handleSave}
          onClose={() => setEditing(null)}
          isNew={isNew}
          allProducts={products}
        />
      )}
    </div>
  );
}

function ProductFormModal({
  product,
  setProduct,
  onSave,
  onClose,
  isNew,
  allProducts,
}: {
  product: AdminProduct;
  setProduct: (p: AdminProduct | null) => void;
  onSave: () => void;
  onClose: () => void;
  isNew: boolean;
  allProducts: AdminProduct[];
}) {
  const update = (partial: Partial<AdminProduct>) => setProduct({ ...product, ...partial });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 backdrop-blur-sm overflow-y-auto py-8 px-4">
      <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl my-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E6E0D7]">
          <h2
            className="text-[#2D2A26] tracking-[0.08em]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "22px", fontWeight: 400 }}
          >
            {isNew ? "New Product" : "Edit Product"}
          </h2>
          <button onClick={onClose} className="text-[#D8D1C7] hover:text-[#2D2A26] transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Name & Slug */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Name" required>
              <input
                value={product.name}
                onChange={(e) => update({ name: e.target.value })}
                className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C] transition-colors"
                style={{ fontSize: "13px" }}
              />
            </FormField>
            <FormField label="Slug">
              <input
                value={product.slug}
                onChange={(e) => update({ slug: e.target.value })}
                placeholder="auto-generated"
                className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7] focus:outline-none focus:border-[#C7A76C] transition-colors"
                style={{ fontSize: "13px" }}
              />
            </FormField>
          </div>

          {/* Price, Category, Tag */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FormField label="Price" required>
              <input
                type="number"
                value={product.price || ""}
                onChange={(e) => update({ price: Number(e.target.value) })}
                className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C] transition-colors"
                style={{ fontSize: "13px" }}
              />
            </FormField>
            <FormField label="Category" required>
              <input
                value={product.category}
                onChange={(e) => update({ category: e.target.value })}
                className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C] transition-colors"
                style={{ fontSize: "13px" }}
                placeholder="e.g. Matcha"
              />
            </FormField>
            <FormField label="Tag">
              <select
                value={product.tag || ""}
                onChange={(e) => update({ tag: e.target.value as AdminProduct["tag"] })}
                className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C] transition-colors"
                style={{ fontSize: "13px" }}
              >
                <option value="">None</option>
                <option value="new">New</option>
                <option value="limited">Limited</option>
                <option value="sold out">Sold Out</option>
              </select>
            </FormField>
          </div>

          {/* Stock */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Stock Quantity">
              <input
                type="number"
                value={product.stock_quantity}
                onChange={(e) => update({ stock_quantity: Number(e.target.value) })}
                className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C] transition-colors"
                style={{ fontSize: "13px" }}
              />
            </FormField>
            <FormField label="Low Stock Threshold">
              <input
                type="number"
                value={product.low_stock_threshold}
                onChange={(e) => update({ low_stock_threshold: Number(e.target.value) })}
                className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C] transition-colors"
                style={{ fontSize: "13px" }}
              />
            </FormField>
          </div>

          {/* Description */}
          <FormField label="Description">
            <textarea
              rows={3}
              value={product.description}
              onChange={(e) => update({ description: e.target.value })}
              className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C] transition-colors resize-none"
              style={{ fontSize: "13px" }}
            />
          </FormField>

          {/* Images */}
          <FormField label="Images (URLs)">
            {product.images.map((img, idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <input
                  value={img}
                  onChange={(e) => {
                    const newImages = [...product.images];
                    newImages[idx] = e.target.value;
                    update({ images: newImages });
                  }}
                  placeholder="Image URL"
                  className="flex-1 px-3 py-2 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7] focus:outline-none focus:border-[#C7A76C] transition-colors"
                  style={{ fontSize: "12px" }}
                />
                <button
                  onClick={() => update({ images: product.images.filter((_, i) => i !== idx) })}
                  className="p-2 text-[#D8D1C7] hover:text-[#8B5C58] transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            <button
              onClick={() => update({ images: [...product.images, ""] })}
              className="text-[#6F7F63] hover:text-[#5F6F53] transition-colors"
              style={{ fontSize: "12px" }}
            >
              + Add Image
            </button>
          </FormField>

          {/* Details */}
          <FormField label="Product Details">
            {product.details.map((detail, idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <input
                  value={detail.label}
                  onChange={(e) => {
                    const newDetails = [...product.details];
                    newDetails[idx] = { ...newDetails[idx], label: e.target.value };
                    update({ details: newDetails });
                  }}
                  placeholder="Label"
                  className="w-1/3 px-3 py-2 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7] focus:outline-none focus:border-[#C7A76C] transition-colors"
                  style={{ fontSize: "12px" }}
                />
                <input
                  value={detail.value}
                  onChange={(e) => {
                    const newDetails = [...product.details];
                    newDetails[idx] = { ...newDetails[idx], value: e.target.value };
                    update({ details: newDetails });
                  }}
                  placeholder="Value"
                  className="flex-1 px-3 py-2 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7] focus:outline-none focus:border-[#C7A76C] transition-colors"
                  style={{ fontSize: "12px" }}
                />
                <button
                  onClick={() => update({ details: product.details.filter((_, i) => i !== idx) })}
                  className="p-2 text-[#D8D1C7] hover:text-[#8B5C58] transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            <button
              onClick={() => update({ details: [...product.details, { label: "", value: "" }] })}
              className="text-[#6F7F63] hover:text-[#5F6F53] transition-colors"
              style={{ fontSize: "12px" }}
            >
              + Add Detail
            </button>
          </FormField>

          {/* Translations */}
          <TranslationsPanel
            fields={[
              { key: "name", label: "Product Name", englishValue: product.name },
              { key: "description", label: "Description", type: "textarea", rows: 3, englishValue: product.description },
            ]}
            i18n={product.i18n || {}}
            onChange={(i18n) => update({ i18n })}
          />

          {/* Related Products */}
          <FormField label="Related Products">
            <div className="space-y-2">
              {allProducts
                .filter((p) => p.id !== product.id)
                .map((p) => (
                  <label key={p.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={product.relatedIds.includes(p.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          update({ relatedIds: [...product.relatedIds, p.id] });
                        } else {
                          update({ relatedIds: product.relatedIds.filter((id) => id !== p.id) });
                        }
                      }}
                      className="w-4 h-4 rounded border-[#E6E0D7] text-[#6F7F63] focus:ring-[#6F7F63]"
                    />
                    <span className="text-[#2D2A26]" style={{ fontSize: "13px" }}>{p.name}</span>
                  </label>
                ))}
            </div>
          </FormField>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#E6E0D7] flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 border border-[#E6E0D7] text-[#6F675F] rounded-lg hover:bg-[#F2EEE8] transition-colors"
            style={{ fontSize: "12px" }}
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-5 py-2.5 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] transition-colors tracking-[0.1em] uppercase"
            style={{ fontSize: "12px" }}
          >
            {isNew ? "Create Product" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[#6F675F] tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px", fontWeight: 400 }}>
        {label}{required && <span className="text-[#8B5C58]"> *</span>}
      </label>
      {children}
    </div>
  );
}